import { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { ErrorResponse, SubError } from 'core/http/response';
import { getErrorMessage } from 'utils/getErrorMessage';
import {
    getIcs2Arrival,
    listIcs2ArrivalMessages,
    submitIcs2Arrival,
    updateIcs2Arrival,
} from 'store/declarations/eu/ics2/arrival/client';
import {
    ICS2ArrivalData,
    ICS2ArrivalNotificationMessage,
    ICS2ArrivalSubmitResult,
    ICS2ArrivalView,
    toIcs2ArrivalData,
} from 'store/declarations/eu/ics2/arrival/ics2-arrival-notification-declaration';

const EDITABLE_STATUSES = ['DRAFT', 'REJECTED'];

const statusOf = (error: unknown): number | undefined =>
    axios.isAxiosError(error) ? error.response?.status : undefined;

export const arrivalFieldErrors = (error: unknown): SubError[] => {
    if (!axios.isAxiosError(error)) return [];
    return (error.response?.data as ErrorResponse | undefined)?.subErrors ?? [];
};

/** The response of a submission rejected by the service's validation, which the errors drawer lists. */
export const arrivalValidationError = (error: unknown): ErrorResponse | undefined => {
    if (!axios.isAxiosError(error)) return undefined;

    const response = error.response?.data as ErrorResponse | undefined;
    return response?.category === 'VALIDATION' ? response : undefined;
};

/**
 * The message to show for a failed arrival call, or `undefined` for the caller's default.
 *
 * The arrival endpoints answer every client error with a domain message the operator can act on -
 * including business rules raised as authorisation errors, such as the notifier's EORI not belonging
 * to the active company, which come back as a 403. `getErrorMessage` masks a 403, so client errors
 * are read here directly and only server and network failures fall back to it.
 */
export const arrivalErrorMessage = (error: unknown): string | undefined => {
    const status = statusOf(error);
    const message = axios.isAxiosError(error)
        ? (error.response?.data as ErrorResponse | undefined)?.message
        : undefined;

    if (status && status >= 400 && status < 500 && message) return message;
    return getErrorMessage(error);
};

/**
 * Owns one ICS2 arrival notification.
 *
 * The aggregate is versioned, so every write has to pin the version it was read at and the result
 * has to be taken back in - which is why saving goes through here rather than through the caller
 * holding a copy of the data.
 */
const useIcs2ArrivalNotification = (initial?: ICS2ArrivalView) => {
    const [arrival, setArrival] = useState<ICS2ArrivalView | undefined>(initial);
    const [messages, setMessages] = useState<ICS2ArrivalNotificationMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    /**
     * The idempotency key of the attempt in flight. A submit whose outcome is unknown has to be
     * retried with the key it was first sent under, so the key is kept until an attempt completes
     * rather than generated per call.
     */
    const submissionKey = useRef<string | undefined>(undefined);

    const load = useCallback(async (id: string) => {
        setIsLoading(true);
        try {
            const view = await getIcs2Arrival(id);
            setArrival(view);
            return view;
        } finally {
            setIsLoading(false);
        }
    }, []);

    const loadMessages = useCallback(async (id: string) => {
        const payload = await listIcs2ArrivalMessages(id);
        setMessages(payload.list ?? []);
        return payload.list ?? [];
    }, []);

    const save = useCallback(
        async (values: ICS2ArrivalData) => {
            if (!arrival?.id) throw new Error('The arrival has to be loaded before it can be saved.');

            setIsSaving(true);
            try {
                const view = await updateIcs2Arrival(arrival.id, arrival.version, toIcs2ArrivalData(values));

                setArrival(view);
                return view;
            } finally {
                setIsSaving(false);
            }
        },
        [arrival?.id, arrival?.version]
    );

    const submit = useCallback(
        async (saved: ICS2ArrivalView): Promise<ICS2ArrivalSubmitResult> => {
            setIsSubmitting(true);
            submissionKey.current = submissionKey.current ?? uuidv4();

            try {
                const result = await submitIcs2Arrival(saved.id, saved.version, submissionKey.current);
                submissionKey.current = undefined;
                await load(saved.id);
                return result;
            } catch (error) {
                // A retry of an uncertain send must reuse the key, so it is only released once the
                // service has given a definite answer about this attempt.
                if (statusOf(error) !== undefined) submissionKey.current = undefined;
                throw error;
            } finally {
                setIsSubmitting(false);
            }
        },
        [load]
    );

    return {
        arrival,
        messages,
        isLoading,
        isSaving,
        isSubmitting,
        isEditable: !arrival?.status || EDITABLE_STATUSES.includes(arrival.status),
        load,
        loadMessages,
        save,
        submit,
        setArrival,
    };
};

export default useIcs2ArrivalNotification;
